import { useCallback, useEffect, useState } from 'react'

/**
 * A handful of screens, so this is a `switch` rather than a router library.
 *
 * The player key travels in the URL *fragment*, which browsers never send to
 * the server — so a seat cannot leak into an access log or a Referer header.
 */
export type Route =
  | { readonly name: 'home' }
  | { readonly name: 'local' }
  | { readonly name: 'queue'; readonly timeControlId: string }
  | { readonly name: 'engine'; readonly timeControlId: string; readonly modelId: string }
  | { readonly name: 'me' }
  | { readonly name: 'arena' }
  | { readonly name: 'room'; readonly roomId: string; readonly playerKey: string | null }

const parse = (): Route => {
  const path = window.location.pathname.replace(/\/+$/, '')
  if (path === '/local') return { name: 'local' }
  if (path === '/me') return { name: 'me' }
  if (path === '/arena') return { name: 'arena' }

  if (path === '/play') return { name: 'queue', timeControlId: timeControlParam('3+2') }

  if (path === '/engine') {
    const query = new URLSearchParams(window.location.search)
    return {
      name: 'engine',
      timeControlId: timeControlParam('10+0'),
      modelId: query.get('m') ?? 'sf-1350',
    }
  }

  const match = /^\/r\/([A-Za-z0-9-]+)$/.exec(path)
  if (match !== null) {
    const fragment = new URLSearchParams(window.location.hash.slice(1))
    return { name: 'room', roomId: match[1] as string, playerKey: fragment.get('k') }
  }
  return { name: 'home' }
}

export const useRoute = (): { readonly route: Route; readonly navigate: (to: string) => void } => {
  const [route, setRoute] = useState<Route>(parse)

  useEffect(() => {
    const update = (): void => setRoute(parse())
    window.addEventListener('popstate', update)
    window.addEventListener('hashchange', update)
    return () => {
      window.removeEventListener('popstate', update)
      window.removeEventListener('hashchange', update)
    }
  }, [])

  const navigate = useCallback((to: string): void => {
    window.history.pushState(null, '', to)
    setRoute(parse())
  }, [])

  return { route, navigate }
}

/**
 * The `tc` query parameter, read safely.
 *
 * `URLSearchParams` decodes `+` as a space, so `?tc=3+2` arrives as "3 2" and
 * every preset with an increment quietly fell back to the default. Building
 * these links with `encodeURIComponent` fixes new ones; turning the space back
 * fixes the ones already bookmarked.
 */
export const timeControlParam = (fallback: string): string => {
  const raw = new URLSearchParams(window.location.search).get('tc')
  return raw === null || raw === '' ? fallback : raw.replaceAll(' ', '+')
}

export const playPath = (timeControlId: string): string =>
  `/play?tc=${encodeURIComponent(timeControlId)}`

export const localPath = (timeControlId: string): string =>
  `/local?tc=${encodeURIComponent(timeControlId)}`

export const enginePath = (timeControlId: string, modelId: string): string =>
  `/engine?tc=${encodeURIComponent(timeControlId)}&m=${encodeURIComponent(modelId)}`

export const roomPath = (roomId: string, playerKey?: string): string =>
  playerKey === undefined ? `/r/${roomId}` : `/r/${roomId}#k=${playerKey}`

export const roomUrl = (roomId: string, playerKey?: string): string =>
  `${window.location.origin}${roomPath(roomId, playerKey)}`
